import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import { CheckIcon, PencilSquareIcon } from "@heroicons/react/24/outline";

import PageHeading from "@components/zenith/ui/PageHeading";
import { useAppContext } from "@context/AppContext";
import {
  invalidateNotifications,
  useAllNotification,
} from "@hooks/query/useAllNotifications";
import { useGetNotification } from "@hooks/query/useGetNotification";
import {
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "@services/notification";
import { formatSchemeDateTime } from "@utils/schemeDay";
import NotificationDetail, {
  ACTION_BUTTON,
  describeNotification,
} from "./NotificationDetail";

/** Notifications shown per "See more". */
const PAGE_SIZE = 20;

const Notifications: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { loggedInInfo, checkPermissions } = useAppContext();
  const queryClient = useQueryClient();
  const walletId = loggedInInfo.userDetails?.walletId;
  // The selection lives in the URL, so the bell and dashboard can link to a notification.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("id");
  const [limit, setLimit] = React.useState(PAGE_SIZE);

  // Same query as the navigation badge, so both show one count.
  const { data: { data: unread = [] } = {}, isSuccess: isCounted } =
    useAllNotification({
      params: { recipientWalletId: walletId, onlyNew: 1, limit: -1 },
      enabled: Boolean(walletId),
    });
  // The API returns the newest first. "See more" asks for a longer first page;
  // keepPreviousData holds the list while it loads.
  const { data: { data: notifications = [], meta } = {}, isLoading } =
    useAllNotification({
      params: { recipientWalletId: walletId, outbox: 1, limit },
      enabled: Boolean(walletId),
      keepPreviousData: true,
    });
  const listed = notifications.find((n: any) => n.id === selectedId);
  // A link can name a notification older than the loaded page: fetch it alone.
  const { data: fetched } = useGetNotification(selectedId ?? "", {
    enabled: Boolean(selectedId) && !isLoading && !listed,
  });
  const selected = listed ?? (fetched?.id === selectedId ? fetched : undefined);

  const invalidate = () => invalidateNotifications(queryClient);
  const { mutate: markAsRead } = useMutation(
    (notificationId: string) => markNotificationAsRead({ notificationId }),
    { onSuccess: invalidate },
  );
  const { mutate: markAllAsRead, isLoading: isMarkingAllAsRead } = useMutation(
    markAllNotificationsAsRead,
    { onSuccess: invalidate },
  );

  // Opening a received notification reads it; once per selection, so marking it unread again sticks.
  const selectedUnreadId =
    selected && !selected.viewedAt && selected.notifiedWalletId === walletId
      ? selected.id
      : undefined;
  React.useEffect(() => {
    if (selectedUnreadId) markAsRead(selectedUnreadId);
  }, [selectedUnreadId, markAsRead]);

  const select = (id?: string) => setSearchParams(id ? { id } : {});

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeading
          title={t("notification.title")}
          sub={
            isCounted &&
            t("notification.unread_count", { count: unread.length })
          }
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => markAllAsRead()}
            disabled={!unread.length || isMarkingAllAsRead}
            className={`btn-default ${ACTION_BUTTON}`}
          >
            <CheckIcon className="h-4 w-4" aria-hidden="true" />
            {t("notification.mark_all_read")}
          </button>
          {checkPermissions(["CreateNotifications"]) ? (
            <Link
              to="/zenith/notifications/new"
              className={`btn-secondary ${ACTION_BUTTON}`}
            >
              <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
              {t("common.compose")}
            </Link>
          ) : null}
        </div>
      </div>
      <div
        className={classNames("grid grid-cols-1 items-start gap-4", {
          "lg:grid-cols-list-detail": selected,
        })}
      >
        {/* On narrow screens the open notification takes the list's place. */}
        <div
          className={classNames("flex-col items-center gap-3", {
            "hidden lg:flex": selected,
            flex: !selected,
          })}
        >
          {!isLoading && !notifications.length ? (
            <p className="w-full rounded-lg border border-gray-200 bg-white px-5 py-8 text-center text-sm text-gray-500">
              {t("notification.empty")}
            </p>
          ) : null}
          <ul
            aria-label={t("notification.title") as string}
            className="w-full overflow-hidden rounded-lg border border-gray-200 bg-white empty:hidden"
          >
            {notifications.map((notification: any) => {
              const item = describeNotification(notification, walletId, t);

              return (
                <li
                  key={item.id}
                  className="border-b border-gray-200 last:border-0"
                >
                  <button
                    type="button"
                    aria-current={item.id === selectedId}
                    onClick={() =>
                      select(item.id === selectedId ? undefined : item.id)
                    }
                    className={classNames(
                      "flex w-full gap-3.5 px-5 py-4 text-left transition-colors",
                      item.id === selectedId
                        ? "bg-primary-1/5"
                        : "hover:bg-gray-50",
                    )}
                  >
                    <span
                      className={classNames(
                        "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                        item.isNew ? "bg-red-500" : "bg-gray-200",
                      )}
                    >
                      {item.isNew ? (
                        <span className="sr-only">{t("common.unread")}</span>
                      ) : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex justify-between gap-3">
                        <span
                          className={classNames(
                            "text-sm text-gray-900",
                            item.isNew ? "font-bold" : "font-medium",
                          )}
                        >
                          {item.title}
                        </span>
                        <span className="whitespace-nowrap text-xs text-gray-500">
                          {formatSchemeDateTime(item.date)}
                        </span>
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-gray-500">
                        {item.subject}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {meta?.total > notifications.length ? (
            <button
              type="button"
              onClick={() => setLimit(shown => shown + PAGE_SIZE)}
              className="text-sm font-semibold text-primary-1 hover:underline"
            >
              {t("common.see_more")}
            </button>
          ) : null}
        </div>
        {selected ? (
          <NotificationDetail
            notification={selected}
            onClose={() => select()}
          />
        ) : null}
      </div>
    </section>
  );
};

export default Notifications;
